/**
 * IntakeFieldRenderer - Renders the appropriate field component based on field type
 */

import React from 'react';
import {
  Field,
  DateField,
  BooleanField,
  DropdownField,
  Label,
} from '../FormFields';
import { MultiSelect } from '@roger/ui-components';
import { applyFormatter } from './formatters';
import { AIReasoningButton, FieldExplanation } from './AIReasoningButton';
import type { IntakeFieldDefinition } from '../../config/intakeFormSchema';

// =============================================================================
// Types
// =============================================================================

export interface IntakeFieldRendererProps {
  field: IntakeFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur: (newValue?: unknown) => void;
  error?: string;
  warning?: boolean;
  showAIExplanations?: boolean;
  // For dropdown type with dynamic options
  dropdownOptions?: Array<{ value: string; label: string }>;
  dropdownLoading?: boolean;
  dropdownError?: string | null;
  dropdownPlaceholder?: string;
  // For AI explanation
  fieldExplanation?: FieldExplanation | null;
  /** Upload name -> display name for intake documents (e.g. for AI reasoning location) */
  documentNameDict?: Record<string, string>;
}

// =============================================================================
// Component
// =============================================================================

export const IntakeFieldRenderer: React.FC<IntakeFieldRendererProps> = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  warning,
  showAIExplanations = false,
  dropdownOptions,
  dropdownLoading,
  dropdownPlaceholder,
  fieldExplanation,
  documentNameDict,
}) => {
  // AI reasoning button component
  const aiReasoningButton = (
    <AIReasoningButton
      fieldId={field.field_id}
      fieldLabel={field.label}
      showAIExplanations={showAIExplanations}
      explanationData={fieldExplanation}
      documentNameDict={documentNameDict}
    />
  );
  switch (field.type) {
    case 'text':
      return (
        <Field
          label={field.label}
          value={(value as string) || ''}
          htmlFor={field.field_id}
          required={field.required}
          warning={warning}
          error={error}
          multiline={field.multiline}
          maintainCursorPosition={!!field.formatter_fn}
          extra={aiReasoningButton}
          onChange={newValue => {
            // Apply formatter on change if defined
            if (field.formatter_fn && newValue) {
              const formattedValue = applyFormatter(
                field.formatter_fn,
                newValue as string
              );
              onChange(formattedValue);
            } else {
              onChange(newValue);
            }
          }}
          onBlur={() => onBlur()}
        />
      );

    case 'date': {
      // Extract year range from validator_config if present
      const yearRange =
        field.validator_config?.fromYear && field.validator_config?.toYear
          ? {
              fromYear: field.validator_config.fromYear as number,
              toYear: field.validator_config.toYear as number,
            }
          : undefined;
      return (
        <DateField
          label={field.label}
          value={value as string | null}
          htmlFor={field.field_id}
          required={field.required}
          warning={warning}
          error={error}
          yearRange={yearRange}
          extra={aiReasoningButton}
          onChange={newValue => {
            onChange(newValue);
          }}
          onBlur={newValue => onBlur(newValue)}
        />
      );
    }

    case 'boolean':
      return (
        <BooleanField
          label={field.label}
          value={value as boolean | null}
          htmlFor={field.field_id}
          required={field.required}
          extra={aiReasoningButton}
          onChange={newValue => {
            onChange(newValue);
          }}
        />
      );

    case 'list': {
      // If options defined, render as multiselect
      if (field.options && field.options.length > 0) {
        const arrayValue = Array.isArray(value) ? (value as string[]) : [];
        return (
          <div>
            <div className='flex justify-between items-center'>
              <Label htmlFor={field.field_id} required={field.required}>
                {field.label}
              </Label>
              {aiReasoningButton}
            </div>
            <MultiSelect
              options={field.options}
              value={arrayValue}
              onChange={newValue => {
                onChange(newValue);
              }}
              orientation='horizontal'
              className='mt-1'
            />
            {error && <p className='mt-1 text-xs text-red-600'>{error}</p>}
          </div>
        );
      }

      // Freeform text list (existing behavior)
      const displayValue = Array.isArray(value)
        ? (value as string[]).join(', ')
        : (value as string) || '';
      return (
        <Field
          label={field.label}
          value={displayValue}
          htmlFor={field.field_id}
          required={field.required}
          warning={warning}
          error={error}
          extra={aiReasoningButton}
          onChange={onChange}
          onBlur={() => onBlur()}
        />
      );
    }

    case 'dropdown':
      // Use formSelect variant for gender dropdown (same style as clinic dropdown)
      return (
        <DropdownField
          label={field.label}
          htmlFor={field.field_id}
          required={field.required}
          value={(value as string) || ''}
          extra={aiReasoningButton}
          onChange={newValue => {
            onChange(newValue);
          }}
          options={dropdownOptions || field.options || []}
          isLoading={dropdownLoading}
          placeholder={dropdownPlaceholder || 'Select an option'}
          enableFiltering={true}
          variant={
            field.field_id === 'patient_gender' ? 'formSelect' : 'combobox'
          }
        />
      );

    default:
      return (
        <div className='text-red-600'>
          Unsupported field type: {(field as IntakeFieldDefinition).type}
        </div>
      );
  }
};
